import { useShallow } from 'zustand/react/shallow'
import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useAuthStore } from '@/stores/auth'
import { ROUTES } from '@/constants/routes'
import { getErrorMessage, getAuthErrorMessage } from '@/types/firebase'
import { SignInHelpModal } from '@/components/landing/SignInHelpModal'
import { getEmailTypoSuggestion } from '@davids-beacon/safety-core'
import { resolvePostLoginPath } from '@/lib/postLoginPath'
import { goToResolvedPath } from '@/lib/lumenHost'

const GoogleLogo = () => (
  <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
)

interface Props {
  onClose: () => void
  onSwitchToRegister?: () => void
}

export function LoginModal({ onClose, onSwitchToRegister }: Props) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { signInWithGoogle, signInWithFacebook, signInWithEmail, sendPasswordReset } = useAuthStore(useShallow((s) => ({ signInWithGoogle: s.signInWithGoogle, signInWithFacebook: s.signInWithFacebook, signInWithEmail: s.signInWithEmail, sendPasswordReset: s.sendPasswordReset })))

  const [signingIn, setSigningIn]         = useState<'google' | 'facebook' | null>(null)
  const [error, setError]                 = useState<string | null>(null)
  const [showForgot, setShowForgot]       = useState(false)
  const [forgotEmail, setForgotEmail]     = useState('')
  const [forgotSending, setForgotSending] = useState(false)
  const [forgotSent, setForgotSent]       = useState(false)
  const [forgotError, setForgotError]     = useState<string | null>(null)
  const [showEmailForm, setShowEmailForm] = useState(false)
  const [emailField, setEmailField]       = useState('')
  const [emailTouched, setEmailTouched]   = useState(false)
  const [passwordField, setPasswordField] = useState('')
  const [emailLoading, setEmailLoading]   = useState(false)
  const [emailError, setEmailError]       = useState<string | null>(null)
  const [showHelp, setShowHelp]           = useState(false)

  const showDevLogin = searchParams.get('devlogin') === '1'
  const [devEmail, setDevEmail]         = useState('')
  const [devPassword, setDevPassword]   = useState('')
  const [devError, setDevError]         = useState('')

  const redirect = searchParams.get('redirect') ?? ''
  const isInvite = redirect.startsWith('/invite/') || redirect.startsWith('/org-invite/')

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  function postLoginRedirect() {
    // roles are on userDoc after signIn* awaits loadUserDocument
    // Org staff → absolute Lumen URL (hard navigation); families stay in-app.
    const roles = useAuthStore.getState().roles()
    goToResolvedPath(resolvePostLoginPath(roles, redirect || null), navigate)
  }

  async function handleSignIn(provider: 'google' | 'facebook') {
    setSigningIn(provider)
    setError(null)
    try {
      if (provider === 'facebook') await signInWithFacebook()
      else await signInWithGoogle()
      postLoginRedirect()
    } catch (err: unknown) {
      setError(getAuthErrorMessage(err))
    } finally {
      setSigningIn(null)
    }
  }

  async function handleForgotPassword() {
    if (!forgotEmail.trim()) return
    setForgotSending(true)
    setForgotError(null)
    try {
      await sendPasswordReset(forgotEmail.trim())
      setForgotSent(true)
    } catch (err: unknown) {
      setForgotError(getErrorMessage(err, 'Could not send reset email'))
    } finally {
      setForgotSending(false)
    }
  }

  async function handleDevEmailLogin() {
    setDevError('')
    try {
      await signInWithEmail(devEmail, devPassword)
      postLoginRedirect()
    } catch (err: unknown) {
      setDevError(getErrorMessage(err, 'Login failed'))
    }
  }

  function toggleEmailForm() {
    setShowEmailForm((v) => !v)
    setEmailError(null)
    setEmailField('')
    setEmailTouched(false)
    setPasswordField('')
  }

  async function handleEmailSubmit() {
    setEmailError(null)
    if (!emailField.trim() || !passwordField) return
    setEmailLoading(true)
    try {
      await signInWithEmail(emailField.trim(), passwordField)
      postLoginRedirect()
    } catch (err: unknown) {
      setEmailError(getAuthErrorMessage(err))
    } finally {
      setEmailLoading(false)
    }
  }

  const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())
  const emailFieldError = emailTouched && emailField && !isValidEmail(emailField) ? 'Enter a valid email address' : null
  const emailFieldValid = emailTouched && isValidEmail(emailField)

  const suggestedEmail = getEmailTypoSuggestion(emailField)
  const emailSuggestion = suggestedEmail ? `Did you mean ${suggestedEmail}?` : null

  function applyEmailSuggestion() {
    if (!suggestedEmail) return
    setEmailField(suggestedEmail)
  }

  const emailFormValid = isValidEmail(emailField) && passwordField.length > 0

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

        {/* Modal card */}
        <div
          className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header band */}
          <div className="px-6 pt-6 pb-5 border-b border-slate-100">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
            >
              <i className="fa-solid fa-xmark" />
            </button>
            <div className="flex items-center gap-3 mb-3">
              <img src="/favicon.svg" alt="" className="w-8 h-8" />
              <div>
                <p className="font-extrabold text-sm text-slate-900 leading-tight">David's Beacon</p>
                <p className="text-[11px] text-slate-400">Safety tools for families</p>
              </div>
            </div>
            {isInvite ? (
              <div className="flex items-start gap-2.5 bg-beacon-50 border border-beacon-200 rounded-xl px-3 py-2.5">
                <i className="fa-solid fa-envelope-open-text text-beacon-500 mt-0.5 flex-shrink-0 text-sm" />
                <div>
                  <p className="text-xs font-bold text-beacon-800">You've been invited!</p>
                  <p className="text-xs text-beacon-700 mt-0.5">Sign in or create an account to accept.</p>
                </div>
              </div>
            ) : (
              <div>
                <h2 className="font-bold text-lg text-slate-900 leading-tight">Welcome back</h2>
                <p className="text-sm text-slate-500 mt-0.5">Sign in to continue</p>
              </div>
            )}
          </div>

          {/* Body */}
          <div className="px-6 py-5 space-y-3">
            {!showEmailForm ? (
              <>
                {error && (
                  <div className="flex items-center gap-2 bg-danger-50 border border-danger-200 rounded-xl px-3 py-2.5">
                    <i className="fa-solid fa-circle-exclamation text-danger-500 text-sm flex-shrink-0" />
                    <p className="text-xs text-danger-700">{error}</p>
                  </div>
                )}

                {/* Social buttons */}
                <div className="space-y-2.5">
                  <button
                    onClick={() => handleSignIn('google')}
                    disabled={!!signingIn}
                    className="w-full flex items-center justify-center gap-3 bg-white border-2 border-slate-200 hover:border-slate-300 text-slate-700 font-semibold rounded-2xl px-4 py-3 shadow-sm hover:shadow-md active:scale-[0.98] transition-all text-sm disabled:opacity-60"
                  >
                    {signingIn === 'google'
                      ? <i className="fa-solid fa-circle-notch animate-spin text-beacon-400 text-base" />
                      : <GoogleLogo />}
                    {signingIn === 'google' ? 'Signing in…' : 'Continue with Google'}
                  </button>
                  <button
                    onClick={() => handleSignIn('facebook')}
                    disabled={!!signingIn}
                    className="w-full flex items-center justify-center gap-3 bg-[#1877F2] hover:bg-[#166FE5] text-white font-semibold rounded-2xl px-4 py-3 shadow-sm hover:shadow-md active:scale-[0.98] transition-all text-sm disabled:opacity-60"
                  >
                    {signingIn === 'facebook'
                      ? <i className="fa-solid fa-circle-notch animate-spin text-base" />
                      : <i className="fa-brands fa-facebook text-lg" />}
                    {signingIn === 'facebook' ? 'Signing in…' : 'Continue with Facebook'}
                  </button>
                </div>

                {/* Divider */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-slate-200" />
                  <span className="text-xs text-slate-400 font-medium">or</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>

                <button
                  onClick={toggleEmailForm}
                  className="w-full flex items-center justify-center gap-2 border-2 border-slate-200 hover:border-beacon-300 hover:bg-beacon-50 text-slate-700 hover:text-beacon-700 font-semibold rounded-2xl px-4 py-3 transition-all text-sm"
                >
                  <i className="fa-solid fa-envelope text-slate-400" />
                  Sign in with email
                </button>
              </>
            ) : (
              <div className="space-y-2.5">
                {showForgot ? (
                  <>
                    {forgotSent ? (
                      <div className="flex flex-col items-center gap-2 py-4 text-center">
                        <i className="fa-solid fa-circle-check text-success-500 text-3xl" />
                        <p className="text-sm font-semibold text-slate-800">Check your inbox</p>
                        <p className="text-xs text-slate-500">We sent a reset link to <span className="font-medium">{forgotEmail}</span></p>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm text-slate-600">Enter your email and we'll send you a reset link.</p>
                        <input
                          type="email"
                          placeholder="Your email address"
                          value={forgotEmail}
                          onChange={(e) => setForgotEmail(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleForgotPassword()}
                          className="w-full border-2 border-slate-200 focus:border-beacon-400 rounded-xl px-4 py-2.5 text-sm focus:outline-none transition-colors"
                        />
                        {forgotError && <p className="text-xs text-danger-500">{forgotError}</p>}
                        <button
                          onClick={handleForgotPassword}
                          disabled={forgotSending || !forgotEmail.trim()}
                          className="w-full bg-beacon-600 hover:bg-beacon-700 disabled:opacity-50 text-white font-semibold rounded-2xl px-4 py-3 transition-colors text-sm"
                        >
                          {forgotSending ? 'Sending…' : 'Send reset link'}
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => { setShowForgot(false); setForgotSent(false); setForgotError(null) }}
                      className="w-full text-sm text-slate-500 hover:text-slate-700 font-medium py-1 transition-colors"
                    >
                      ← Back to sign in
                    </button>
                  </>
                ) : (
                  <>
                    <div className="space-y-2">
                      <div className="relative">
                        <input
                          type="email"
                          placeholder="Email address"
                          value={emailField}
                          onChange={(e) => setEmailField(e.target.value)}
                          onBlur={() => setEmailTouched(true)}
                          onKeyDown={(e) => e.key === 'Enter' && handleEmailSubmit()}
                          className={`w-full border-2 rounded-xl px-4 py-2.5 pr-9 text-sm focus:outline-none transition-colors ${emailFieldError ? 'border-danger-400 focus:border-danger-400' : emailFieldValid ? 'border-success-400 focus:border-success-400' : 'border-slate-200 focus:border-beacon-400'}`}
                        />
                        {emailFieldValid && !emailFieldError && (
                          <i className="fa-solid fa-circle-check text-success-500 text-sm absolute right-3 top-1/2 -translate-y-1/2" />
                        )}
                      </div>
                      {emailFieldError && <p className="text-xs text-danger-500">{emailFieldError}</p>}
                      {!emailFieldError && emailSuggestion && (
                        <p className="text-xs text-warning-600">
                          {emailSuggestion}{' '}
                          <button onClick={applyEmailSuggestion} className="underline font-semibold">Fix it</button>
                        </p>
                      )}
                      <input
                        type="password"
                        placeholder="Password"
                        value={passwordField}
                        onChange={(e) => setPasswordField(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleEmailSubmit()}
                        className="w-full border-2 border-slate-200 focus:border-beacon-400 rounded-xl px-4 py-2.5 text-sm focus:outline-none transition-colors"
                      />
                    </div>
                    {emailError && (
                      <div className="flex items-center gap-2 bg-danger-50 border border-danger-200 rounded-xl px-3 py-2">
                        <i className="fa-solid fa-circle-exclamation text-danger-500 text-xs flex-shrink-0" />
                        <p className="text-xs text-danger-700">{emailError}</p>
                      </div>
                    )}
                    <button
                      onClick={handleEmailSubmit}
                      disabled={emailLoading || !emailFormValid}
                      className="w-full bg-beacon-600 hover:bg-beacon-700 disabled:opacity-50 text-white font-semibold rounded-2xl px-4 py-3 transition-colors text-sm"
                    >
                      {emailLoading ? 'Signing in…' : 'Sign in'}
                    </button>
                    <div className="flex items-center justify-between pt-1">
                      <button
                        onClick={toggleEmailForm}
                        className="text-sm text-slate-500 hover:text-slate-700 font-medium transition-colors"
                      >
                        ← Other options
                      </button>
                      <button
                        onClick={() => { setShowForgot(true); setForgotSent(false); setForgotError(null) }}
                        className="text-sm text-beacon-600 hover:text-beacon-700 font-medium transition-colors"
                      >
                        Forgot password?
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Footer band */}
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
            <button
              onClick={() => {
                if (onSwitchToRegister) { onSwitchToRegister() }
                else { onClose(); navigate(isInvite ? `${ROUTES.REGISTER}?redirect=${encodeURIComponent(redirect)}` : ROUTES.REGISTER) }
              }}
              className="text-sm font-semibold text-beacon-600 hover:text-beacon-700 transition-colors"
            >
              Create account →
            </button>
            <button
              onClick={() => setShowHelp(true)}
              className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
            >
              Need help?
            </button>
          </div>

          {import.meta.env.DEV && showDevLogin && (
            <div className="px-6 pb-5 space-y-2" data-testid="devlogin">
              <div className="h-px bg-slate-100" />
              <input type="email" placeholder="Email" value={devEmail} onChange={(e) => setDevEmail(e.target.value)} data-testid="devlogin-email" className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none" />
              <input type="password" placeholder="Password" value={devPassword} onChange={(e) => setDevPassword(e.target.value)} data-testid="devlogin-password" className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none" />
              {devError && <p className="text-xs text-danger-500">{devError}</p>}
              <button onClick={handleDevEmailLogin} className="w-full bg-slate-800 text-white text-sm font-semibold rounded-2xl px-4 py-3">Sign in (dev)</button>
            </div>
          )}
        </div>
      </div>

      {showHelp && <SignInHelpModal onClose={() => setShowHelp(false)} />}
    </>
  )
}
